import type React from 'react';

import { ArrowInCircleRightIcon, DetectionIcon, InfoCircleIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { calcTagType } from '@vlabs/shared/utils';
import {
  useWindowSize, Tooltip, CopyButton, FaceSample,
} from '@vlabs/uikit';
import { BaseSamplePlaceholder } from '@vlabs/uikit/components/sample/base-sample/BaseSamplePlaceholder';
import type { WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import {
  useCardQuery, useFaceAttributeSamplesQuery,
} from '@vlabs/monokit/entities/events/api';
import type { EventListPermissions } from '@vlabs/monokit/entities/events/event-list-table/EventListPermissions';
import { GoToCardCircleAnimatedButton } from '@vlabs/monokit/features';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { EmptyCard } from '@vlabs/monokit/shared/ui/empty-card';

import { useTopMatchCardCellTranslation } from './i18n';
import st from './TopMatchCardCell.module.sass';

interface TopMatchCardCellProps extends WithDataTestid {
  topMatch: Record<string, unknown>;
  permissions?: Partial<EventListPermissions>;
}

const displayName = 'TopMatchCardCell';
const testid = buildTestid(displayName);

function TopMatchCardCell({
  topMatch,
  'data-testid': dataTestId = undefined,
  permissions = {},
}: TopMatchCardCellProps): React.JSX.Element {
  const { t } = useTopMatchCardCellTranslation();
  const canReadCard = permissions.canReadCard ?? true;
  const canViewCardDetails = permissions.canViewCardDetails ?? true;

  const faceId = topMatch.face_id as string | undefined;
  const cardId = (topMatch.card_id ?? topMatch.external_id) as string | undefined;
  const similarity = topMatch.similarity as number | undefined;

  const { data: card } = useCardQuery(cardId);
  const { data: samplesData } = useFaceAttributeSamplesQuery(faceId);

  const { width } = useWindowSize();
  const smallWidth = width && width < 1920;

  if (!canReadCard) {
    return (
      <EmptyCard
        className={cn(st.Empty, { [st.Empty_small]: !!smallWidth })}
        text=""
      />
    );
  }

  if (!card) {
    return (
      <EmptyCard
        className={cn(st.Empty, { [st.Empty_small]: !!smallWidth })}
        dataTip={t('no_data_tooltip')}
        text={t('no_data')}
      />
    );
  }

  const src = samplesData?.samples?.[0]?.uiSampleURL;

  const kind = calcTagType(similarity ?? 0);
  const fio = card.user_data?.fio;

  let buttonOnTop;
  if (cardId && canViewCardDetails) {
    buttonOnTop = (
      <GoToCardCircleAnimatedButton
        cardId={cardId}
        icon={<ArrowInCircleRightIcon />}
        size="small"
      />
    );
  }

  return (
    <div
      className={cn(st.TopMatchCell, {
        [st.TopMatchCell_small]: !!smallWidth,
        [st.TopMatchCell_positive]: kind === 'positive',
        [st.TopMatchCell_attention]: kind === 'attention',
        [st.TopMatchCell_negative]: kind === 'negative',
      })}
      {...testid(dataTestId)}
    >
      <FaceSample
        buttonOnTop={buttonOnTop}
        className={cn(st.Sample, { [st.Sample_small]: !!smallWidth })}
        placeholder={(
          <BaseSamplePlaceholder
            placeholderType="image"
            size="large"
            tooltip={(
              <Tooltip tooltipContent={t('no_face')}>
                <InfoCircleIcon />
              </Tooltip>
            )}
          />
        )}
        size={smallWidth ? 'medium' : 'large'}
        src={src}
      />

      <div className={cn(st.Info, { [st.Info_small]: !!smallWidth })}>
        <SimilarityTag
          icon={<DetectionIcon />}
          size={smallWidth ? 'medium' : 'large'}
          title={t('tag')}
          value={similarity ?? 0}
        />

        {!!fio && (
          <div className={st.InfoRow}>
            <span className={cn(st.Title, { [st.Title_small]: !!smallWidth })}>
              {t('fio')}
            </span>
            <span className={cn(st.Value, { [st.Value_small]: !!smallWidth })}>
              {fio}
            </span>
          </div>
        )}

        <div className={st.InfoRow}>
          <span className={cn(st.Title, { [st.Title_small]: !!smallWidth })}>
            {t('card_id')}
          </span>
          <div className={st.CardId}>
            <span className={cn(st.Value, { [st.Value_small]: !!smallWidth })}>
              <TruncatedText
                hasTruncation
                tooltipContent={cardId}
              >
                {cardId}
              </TruncatedText>
            </span>

            {!!cardId && (
              <CopyButton
                className={st.CopyButton}
                size="small"
                value={cardId}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

TopMatchCardCell.displayName = displayName;

export { TopMatchCardCell };
